import React from 'react';
import { InvoiceData, InvoiceSettings } from '../../../types';

interface Props {
  data: InvoiceData;
  settings: InvoiceSettings;
  labels: Record<string, string>;
  subtotal: number;
  taxTotal: number;
  total: number;
}

const BoldTemplate: React.FC<Props> = ({ data, settings, total }) => {
  const currencySymbol = settings.currencySymbol || '$';

  return (
    <div className="max-w-4xl mx-auto bg-slate-900 text-white min-h-screen flex flex-col font-sans">
      <div className="bg-brand-500 text-slate-950 p-16 flex justify-between items-end">
        <div className="flex items-end gap-6">
          {data.logo && (
            <img src={data.logo} alt="Logo" className="h-20 w-auto object-contain mb-1" />
          )}
          <div>
            <h1 className="text-8xl font-black tracking-tighter uppercase leading-none">Bill</h1>
            <p className="text-xl font-bold mt-4 tracking-tight">Invoice No. {data.number}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-black text-2xl tracking-tighter mb-2">{data.senderName}</p>
          <p className="text-sm font-bold opacity-80 leading-relaxed whitespace-pre-wrap">
            {data.senderAddress}
          </p>
        </div>
      </div>

      <div className="p-16 flex-1">
        <div className="mb-20 flex justify-between">
          <div className="max-w-xs">
            <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-500 mb-4">
              Recipient
            </h4>
            <p className="text-3xl font-black mb-2 leading-none">
              {data.recipientCompany || data.recipientName}
            </p>
            <p className="text-slate-400 text-sm font-medium whitespace-pre-wrap">
              {data.recipientAddress}
            </p>
          </div>
          <div className="text-right">
            <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-500 mb-4">
              Date Issued
            </h4>
            <p className="text-xl font-bold">{data.date}</p>
          </div>
        </div>

        <table className="w-full mb-20 border-collapse">
          <thead>
            <tr className="border-b-4 border-white">
              <th className="text-left py-6 text-xs font-black uppercase tracking-widest">
                Item Description
              </th>
              <th className="text-right py-6 text-xs font-black uppercase tracking-widest w-32">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {data.items.map((item, idx) => (
              <tr key={idx} className="break-inside-avoid">
                <td className="py-8">
                  <p className="text-2xl font-black">{item.description}</p>
                  <p className="text-brand-400 text-xs font-bold mt-1 uppercase tracking-widest">
                    {item.quantity} x {currencySymbol}
                    {item.rate}
                  </p>
                </td>
                <td className="py-8 text-right text-3xl font-black">
                  {currencySymbol}
                  {(item.quantity * item.rate).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-between items-end border-t-8 border-brand-500 pt-10 break-inside-avoid">
          <div className="max-w-xs">
            <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-500 mb-4">
              Terms & Info
            </h4>
            <p className="text-xs text-slate-400 leading-loose">{data.paymentInfo}</p>
          </div>
          <div className="text-right">
            <p className="text-brand-500 font-black text-sm uppercase tracking-widest mb-2">
              Grand Total
            </p>
            <p className="text-7xl font-black tracking-tighter">
              {currencySymbol}
              {total.toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoldTemplate;
